import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SafeIcon from '../common/SafeIcon';

export default function PrivacyPolicy() {
  const effectiveDate = "December 1, 2025";
  
  const tocItems = [
    { id: 'information', title: 'Information We Collect' },
    { id: 'use', title: 'How We Use Your Information' },
    { id: 'share', title: 'How We Share Your Information' },
    { id: 'rights', title: 'Your Data Protection Rights' },
    { id: 'changes', title: 'Changes to This Privacy Policy' },
    { id: 'contact', title: 'Contact Us' }
  ];

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle smooth scroll for TOC to avoid HashRouter conflicts and account for fixed navbar
  const handleScrollToSection = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const navbarHeight = 80; // height of fixed navbar (h-20 = 80px)
      const y = element.getBoundingClientRect().top + window.scrollY - navbarHeight - 32; // 32px extra padding
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-dc-bg text-dc-text">
      <Navbar />
      
      <main className="flex-grow pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Legal Hero */}
          <div className="mb-16 text-center">
            <div className="inline-block bg-dc-teal-deep/5 text-dc-teal-signal text-xs font-michroma px-4 py-2 rounded-full mb-6 border border-dc-teal-signal/20">
              LEGAL
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Privacy Policy</h1>
            <p className="text-lg text-dc-text/70 max-w-2xl mx-auto mb-4">
              At RepUpgrade, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy outlines how we collect, use, and safeguard your data when you use our services.
            </p>
            <p className="text-sm text-dc-text/50">
              Effective Date: {effectiveDate}
            </p>
          </div>

          {/* Table of Contents */}
          <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-soft mb-16">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <SafeIcon name="List" className="w-5 h-5 text-dc-teal-signal" />
              Table of Contents
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {tocItems.map((item, index) => (
                <a 
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={(e) => handleScrollToSection(e, item.id)}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-dc-bg transition-colors group cursor-pointer"
                >
                  <span className="text-dc-teal-signal font-michroma text-sm mt-1 group-hover:text-dc-teal-deep">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="text-dc-text group-hover:text-dc-teal-signal font-medium">
                    {item.title}
                  </span>
                </a>
              ))}
            </div>
          </div>

          {/* Privacy Policy Sections */}
          <div className="space-y-16">
            <section id="information">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                1. Information We Collect
              </h2>
              <div className="space-y-6 text-dc-text/80 leading-relaxed">
                <p>
                  RepUpgrade collects various types of personal data to provide and improve our services, ensuring a seamless and secure experience for our users. We are committed to transparency in how we handle your information.
                </p>
                
                <div>
                  <h3 className="font-bold text-dc-text mb-3 text-lg">Personal Identifiers</h3>
                  <p>
                    Includes your name, username, unique user IDs, and any other data directly identifying you. This is collected when you register for an account.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-bold text-dc-text mb-3 text-lg">Contact Information</h3>
                  <p>
                    Such as your email address, phone number, and postal address. Used for communication, support, and service delivery.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-bold text-dc-text mb-3 text-lg">Usage Data</h3>
                  <p>
                    Information about how you interact with our services, including IP address, browser type, operating system, pages visited, and timestamps. Collected via cookies and similar technologies.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-bold text-dc-text mb-3 text-lg">Financial Information</h3>
                  <p>
                    If you make purchases through RepUpgrade, we collect payment details like credit card numbers, billing address, and transaction history. This is processed securely via third-party payment processors.
                  </p>
                </div>
                
                <div className="p-5 bg-dc-teal-deep/5 border border-dc-teal-signal/20 rounded-xl">
                  <p>
                    <span className="font-bold text-dc-teal-deep">Data Collection Methods:</span> Data is primarily collected through direct input from you (e.g., account registration, form submissions) and automatically through cookies and similar technologies (e.g., usage data). In some cases, we may receive data from third-party sources to enhance our services and for verification purposes.
                  </p>
                </div>
              </div>
            </section>

            <section id="use">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                2. How We Use Your Information
              </h2>
              <div className="space-y-6 text-dc-text/80 leading-relaxed">
                <div>
                  <h3 className="font-bold text-dc-text mb-3 text-lg">To Provide and Maintain Our Services</h3>
                  <p>
                    We use your information to operate, maintain, and provide the features and functionality of our reputation management services, including monitoring online mentions, generating reports, and facilitating content removal requests.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-bold text-dc-text mb-3 text-lg">To Improve User Experience</h3>
                  <p>
                    We analyze how users interact with our website and services to identify areas for improvement, optimize features, and personalize your experience. This includes understanding user preferences and troubleshooting issues.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-bold text-dc-text mb-3 text-lg">To Communicate With You</h3>
                  <p>
                    We use your contact information to send you service-related notifications, updates, security alerts, and to respond to your inquiries or support requests. You may opt out of non-essential communications.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-bold text-dc-text mb-3 text-lg">For Marketing and Promotional Purposes</h3>
                  <p>
                    With your consent where required, we may use your information to send you marketing communications, special offers, or newsletters about our services. You can opt out of receiving marketing emails at any time.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-bold text-dc-text mb-3 text-lg">For Legal Compliance and Security</h3>
                  <p>
                    We may use your information to comply with legal obligations, enforce our terms and conditions, protect our rights and the rights of our users, and prevent fraud or other illegal activities. This includes data necessary for audits and record-keeping.
                  </p>
                </div>
              </div>
            </section>

            <section id="share">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                3. How We Share Your Information
              </h2>
              <div className="space-y-6 text-dc-text/80 leading-relaxed">
                <p>
                  RepUpgrade is committed to protecting your privacy. We only share your data under specific circumstances, always with appropriate safeguards, and only when necessary to provide our services, meet legal obligations, or for legitimate business operations.
                </p>
                
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                    <h3 className="font-bold text-dc-text mb-3 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-dc-teal-signal/10 flex items-center justify-center">
                        <SafeIcon name="Users" className="w-4 h-4 text-dc-teal-signal" />
                      </div>
                      Service Providers
                    </h3>
                    <p className="text-sm">
                      We may share data with third-party vendors and service providers who perform services on our behalf, such as hosting, data analysis, payment processing, and customer support. These parties are contractually bound to protect your information.
                    </p>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                    <h3 className="font-bold text-dc-text mb-3 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-dc-teal-signal/10 flex items-center justify-center">
                        <SafeIcon name="Briefcase" className="w-4 h-4 text-dc-teal-signal" />
                      </div>
                      Business Partners
                    </h3>
                    <p className="text-sm">
                      We may collaborate with trusted business partners to offer integrated services or joint promotions. In such cases, data is shared only when explicitly necessary for the partnership and with your consent where required.
                    </p>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                    <h3 className="font-bold text-dc-text mb-3 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-dc-teal-signal/10 flex items-center justify-center">
                        <SafeIcon name="Gavel" className="w-4 h-4 text-dc-teal-signal" />
                      </div>
                      Legal Requirements
                    </h3>
                    <p className="text-sm">
                      We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court order or government agency). This includes complying with legal obligations or protecting our rights.
                    </p>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                    <h3 className="font-bold text-dc-text mb-3 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-dc-teal-signal/10 flex items-center justify-center">
                        <SafeIcon name="Share2" className="w-4 h-4 text-dc-teal-signal" />
                      </div>
                      Business Transfers
                    </h3>
                    <p className="text-sm">
                      In the event of a merger, acquisition, or sale of assets, your personal information may be transferred to the acquiring entity. We will notify you of any such transfer and choices you may have regarding your data.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section id="rights">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                4. Your Data Protection Rights
              </h2>
              <div className="space-y-6 text-dc-text/80 leading-relaxed">
                <p>
                  At RepUpgrade, we are committed to protecting your personal data. This section outlines your rights regarding the data we hold about you and how you can exercise those rights.
                </p>
                
                <div className="space-y-4">
                  {[
                    { title: "The Right to Access", desc: "You have the right to request copies of your personal data. We may charge you a small fee for this service." },
                    { title: "The Right to Rectification", desc: "You have the right to request that RepUpgrade correct any information you believe is inaccurate. You also have the right to request us to complete information you believe is incomplete." },
                    { title: "The Right to Erasure", desc: "You have the right to request that RepUpgrade erase your personal data, under certain conditions." },
                    { title: "The Right to Restriction of Processing", desc: "You have the right to request that RepUpgrade restrict the processing of your personal data, under certain conditions." },
                    { title: "The Right to Data Portability", desc: "You have the right to request that RepUpgrade transfer the data that we have collected to another organization, or directly to you, under certain conditions." },
                    { title: "The Right to Object to Processing", desc: "You have the right to object to RepUpgrade's processing of your personal data, under certain conditions." }
                  ].map((right, i) => (
                    <div key={i} className="flex items-start gap-4 p-4 rounded-xl hover:bg-white border border-transparent hover:border-gray-100 hover:shadow-sm transition-all">
                      <div className="w-8 h-8 rounded-full bg-dc-teal-signal/10 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-sm font-michroma text-dc-teal-signal">{i + 1}</span>
                      </div>
                      <div>
                        <h3 className="font-bold text-dc-text mb-1">{right.title}</h3>
                        <p className="text-sm">{right.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="bg-dc-teal-deep text-white rounded-xl p-8 mt-6">
                  <h3 className="font-bold mb-3 flex items-center gap-2 text-xl">
                    <SafeIcon name="Mail" className="w-5 h-5 text-dc-gold" />
                    Contact Us to Exercise Your Rights
                  </h3>
                  <p className="text-white/80">
                    To exercise any of these rights, please contact us using the information provided in the "Contact Us" section below. We will respond to your request within 30 days.
                  </p>
                </div>
              </div>
            </section>

            <section id="changes">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                5. Changes to This Privacy Policy
              </h2>
              <div className="space-y-4 text-dc-text/80 leading-relaxed">
                <p>
                  We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page. We will let you know via email and/or a prominent notice on our Service, prior to the change becoming effective and update the 'Effective Date' at the top of this Privacy Policy.
                </p>
                <p>
                  You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
                </p>
                <p className="font-medium text-dc-text">
                  Effective Date: {effectiveDate}
                </p>
              </div>
            </section>

            <section id="contact">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                6. Contact Us
              </h2>
              <div className="space-y-6 text-dc-text/80 leading-relaxed">
                <p>
                  If you have any questions or concerns regarding our Privacy Policy, please do not hesitate to reach out. Your privacy is important to us, and we are here to assist you.
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-dc-teal-deep flex items-center justify-center">
                        <SafeIcon name="Mail" className="text-dc-gold w-6 h-6" />
                      </div>
                      <h3 className="font-bold text-dc-text text-lg">Email Address</h3>
                    </div>
                    <a href="mailto:jmoore@repupgrade.com" className="text-dc-teal-signal hover:text-dc-teal-deep font-medium transition-colors">
                      jmoore@repupgrade.com
                    </a>
                  </div>
                  
                  <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-dc-teal-deep flex items-center justify-center">
                        <SafeIcon name="MapPin" className="text-dc-gold w-6 h-6" />
                      </div>
                      <h3 className="font-bold text-dc-text text-lg">Mailing Address</h3>
                    </div>
                    <p className="text-dc-text/80">
                      RepUpgrade Inc.<br />
                      104 S Main, Aberdeen, SD DC 57401, USA
                    </p>
                  </div>
                </div>
                
                <div className="text-center mt-10">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center gap-2 bg-dc-orange hover:bg-dc-orange-hover text-white font-semibold py-4 px-8 rounded-xl shadow-lg transition-all hover:-translate-y-1"
                  >
                    Go to Contact Page
                    <SafeIcon name="ArrowRight" className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            </section>
          </div>

          {/* Back to Home CTA */}
          <div className="mt-20 pt-10 border-t border-gray-200">
            <div className="bg-dc-bg rounded-3xl p-10 text-center">
              <h2 className="text-2xl font-bold text-dc-text mb-4">Return to RepUpgrade</h2>
              <p className="text-dc-text/70 mb-8 max-w-2xl mx-auto">
                Back to building trust, content, and clear next steps for your local business.
              </p>
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 bg-white border border-gray-200 hover:border-dc-teal-signal/30 text-dc-text font-semibold py-3 px-8 rounded-xl shadow-sm transition-all"
              >
                <SafeIcon name="ArrowLeft" className="w-4 h-4" />
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}